import React, { useState } from "react";
import clsx from "clsx";
import { ChevronDown, ChevronUp } from "lucide-react";
import Pagination from "../Pagination";
import { styles } from "./styles";

export interface Column<T> {
  key: keyof T | string;
  label: string;
  render?: (row: T) => React.ReactNode;
}

interface PaginationConfig {
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onSizeChange: (size: number) => void;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  emptyState?: React.ReactNode;
  detailRenderer?: (row: T) => React.ReactNode;
  pagination?: PaginationConfig;
  onRowClick?: (row: T) => void;
}

export function DataTable<T extends { id: number | string }>({
  data,
  columns,
  loading = false,
  emptyState = <DefaultEmpty />,
  detailRenderer,
  pagination,
  onRowClick,
}: DataTableProps<T>) {
  const [expanded, setExpanded] = useState<number | string | null>(null);

  const toggle = async (row: T) => {
    if (onRowClick && expanded !== row.id) {
      onRowClick(row);
    }
    setExpanded(expanded === row.id ? null : row.id);
  };

  return (
    <div className={styles.container}>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr className={styles.theadRow}>
              {columns.map((col) => (
                <th key={String(col.key)} className={styles.th}>
                  {col.label}
                </th>
              ))}
              <th className="w-[40px] border-b border-gray-200"></th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr className={styles.loaderRow}>
                <td colSpan={columns.length + 1} className={styles.loaderCell}>
                  <div className={styles.loader}>
                    <div className={styles.spinner} />
                  </div>
                </td>
              </tr>
            )}

            {!loading && data.length === 0 && (
              <tr className={styles.emptyRow}>
                <td colSpan={columns.length + 1} className={styles.emptyCell}>
                  <div className={styles.emptyWrapper}>{emptyState}</div>
                </td>
              </tr>
            )}

            {!loading &&
              data.length > 0 &&
              data.map((row, index) => {
                const isExpanded = expanded === row.id;

                return (
                  <React.Fragment key={row.id}>
                    <tr
                      className={clsx(
                        styles.rowBase,
                        isExpanded ? styles.rowExpanded : styles.rowNormal
                      )}
                      onClick={() => toggle(row)}
                    >
                      {columns.map((col) => {
                        const raw = col.render ? col.render(row) : (row as any)[col.key];
                        const safe = sanitize(raw);

                        return (
                          <td
                            key={String(col.key)}
                            className={clsx(
                              styles.cellBase,
                              "border-b border-gray-200"
                              // index !== data.length - 1 && "border-b border-gray-200"
                            )}
                          >
                            {safe}
                          </td>
                        );
                      })}

                      <td
                        className={clsx(
                          styles.chevronCell,
                          "border-b border-gray-200"
                          // index !== data.length - 1 && "border-b border-gray-200"
                        )}
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-gray-700" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-700" />
                        )}
                      </td>
                    </tr>

                    {/* DETAIL ROW */}
                    <tr
                      className={clsx(
                        styles.detailRow,
                        isExpanded ? styles.detailExpanded : styles.detailCollapsed
                      )}
                    >
                      <td colSpan={columns.length + 1} className="p-0 bg-blue-50">
                        {isExpanded && (
                          <div className={styles.detailWrapper}>{detailRenderer?.(row)}</div>
                        )}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </div>

      {pagination && <Pagination pagination={pagination} />}
    </div>
  );
}

function sanitize(v: any): React.ReactNode {
  if (typeof v === "string" || typeof v === "number") return v;
  if (React.isValidElement(v)) return v;
  if (v === null || v === undefined) return "—";
  if (Array.isArray(v)) return v.join(", ");
  return "—";
}

const DefaultEmpty = () => <div className="text-gray-500 text-base">No data available</div>;

export default DataTable;
